import { PbProductType } from '@/enums/PbProductType'
import { isCartSubItem } from '@/guards'
import type {
	CartItemAndLineItem,
	CartSubItemAndLineItem,
} from '@/mixins/merchandise'
import type { CustomCartItem } from '@/types'

export const REVIEW_SLIDE_CATEGORY_MAP: Record<string, PbProductType[]> = {
	funeralService: [
		PbProductType.Service,
		PbProductType.Package,
		PbProductType.Transportation,
		PbProductType.Oap,
		PbProductType.Disposition,
	],
	funeralMerchandise: [PbProductType.Merchandise],
	essentials: [
		PbProductType.CashAdvance,
		PbProductType.FamilyTravel,
		PbProductType.Cemetery,
		PbProductType.Other,
		PbProductType.Afterward,
		PbProductType.Everplans,
	],
}

export type ReviewSlideSection = keyof typeof REVIEW_SLIDE_CATEGORY_MAP
export type ReviewSlideCartEntry = CartItemAndLineItem | CartSubItemAndLineItem

function itemMatchesCategories(
	category: PbProductType | undefined,
	categories: readonly PbProductType[]
): boolean {
	return category !== undefined && categories.includes(category)
}

export function getReviewSlideCartItemsBySection(
	items: CartItemAndLineItem[],
	section: ReviewSlideSection
): ReviewSlideCartEntry[] {
	const categories = REVIEW_SLIDE_CATEGORY_MAP[section]
	const result: ReviewSlideCartEntry[] = []

	for (const item of items) {
		const category = item.lineItem?.category as PbProductType | undefined

		if (category === PbProductType.Package) {
			const matchingSubItems = item.subItems.filter(
				(subItem: CartSubItemAndLineItem) => {
					return (
						!subItem.lineItem?.isVirtualLine &&
						subItem.lineItem?.category !== PbProductType.Disposition &&
						itemMatchesCategories(
							subItem.lineItem?.category as PbProductType,
							categories
						)
					)
				}
			) as CartSubItemAndLineItem[]

			if (matchingSubItems.length > 0) {
				result.push(...matchingSubItems)
			}
		} else if (itemMatchesCategories(category, categories)) {
			result.push(item)
		}
	}

	return result
}

export function getReviewSlideCustomCartItemsBySection(
	items: CustomCartItem[],
	section: ReviewSlideSection
): CustomCartItem[] {
	const categories = REVIEW_SLIDE_CATEGORY_MAP[section]
	const result: CustomCartItem[] = []

	for (const item of items) {
		if (itemMatchesCategories(item.category, categories)) {
			result.push(item)
		}
	}

	return result
}

export function getReviewSlideCartEntryKey(
	entry: ReviewSlideCartEntry
): string {
	if (isCartSubItem(entry)) {
		return `${entry.packageLineItemId}-${entry.lineItemId}`
	}

	return entry.lineItemId
}
